import {
    fetchAuthSession,
    fetchUserAttributes,
    type FetchUserAttributesOutput,
    getCurrentUser,
    signInWithRedirect,
    signOut
} from '@aws-amplify/auth'
import * as Sentry from '@sentry/browser'
import {isDefined, ManualPromise} from '@oc/utils'
import {action, autorun, IReactionDisposer, makeObservable, observable} from 'mobx'
import {Authentication} from './authentication'

const infinitePromise = new ManualPromise()

export class CognitoAuthentication implements Authentication {
    @observable.ref private userAttributes?: FetchUserAttributesOutput
    @observable private _username?: string

    private disposeAutorun?: IReactionDisposer

    public get username(): string {
        if (!this._username) {
            throw new Error('User not logged in yet')
        }

        return this._username
    }

    public get email() {
        return this.userAttributes?.email
    }

    public get name() {
        return (
            this.userAttributes?.preferred_username ??
            [this.userAttributes?.given_name, this.userAttributes?.middle_name, this.userAttributes?.family_name]
                .filter(isDefined)
                .join(' ')
        )
    }

    public get isLoggedIn() {
        return typeof this._username !== 'undefined'
    }

    public constructor() {
        makeObservable(this)

        this.getIdToken = this.getIdToken.bind(this)

        autorun(
            () => {
                if (this._username) {
                    Sentry.setUser({
                        username: this._username,
                        email: this.email
                    })
                } else {
                    Sentry.setUser(null)
                }
            },
            {
                requiresObservable: true
            }
        )
    }

    @action.bound
    public async signIn(currentPathname: string): Promise<void> {
        const session = await fetchAuthSession()

        if (!session.tokens) {
            await signInWithRedirect({
                customState: currentPathname
            })

            await infinitePromise
        }

        const [user] = await Promise.all([getCurrentUser(), this.reloadUserAttributes()])
        this._username = user.username
    }

    @action.bound
    public async signOut(): Promise<void> {
        for (let index = sessionStorage.length - 1; index >= 0; index--) {
            const key = sessionStorage.key(index)
            if (key?.startsWith('s2a-token-exchange-')) {
                sessionStorage.removeItem(key)
            }
        }

        this._username = undefined
        this.userAttributes = undefined

        await signOut({
            global: true
        })

        await infinitePromise
    }

    @action.bound
    public async reloadUserAttributes(): Promise<void> {
        this.userAttributes = await fetchUserAttributes()
    }

    public async getIdToken(): Promise<string> {
        const idToken = (await fetchAuthSession()).tokens?.idToken
        if (typeof idToken === 'undefined') {
            throw new Error('ID token is undefined')
        }

        return idToken.toString()
    }
}
